import React, { useState, useEffect, useCallback } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { getSeoMeta } from "./seoMeta";
import { GlobalStyle } from "./theme";
import { ToastProvider } from "./Toast";
import Navbar from "./Navbar";
import MarketList from "./MarketList";
import MarketDetail from "./MarketDetail";
import PositionList from "./PositionList";
import TradeModal from "./TradeModal";
import AuthModal from "./AuthModal";
import WalletPage from "./WalletPage";
import AdminDashboard from "./AdminDashboard";
import EmployeeDashboard from "./EmployeeDashboard";
import MyTickets from "./MyTickets";
import HowItWorks from "./HowItWorks";
import ContactModal from "./ContactModal";
import Footer from "./Footer";
import LegalPages from "./LegalPages";
import CompanyPages from "./CompanyPages";
import ResourcePages from "./ResourcePages";
import AccountSettings from "./AccountSettings";
import { getProfile } from "./accountClient";
import { useI18n } from "./i18n";
import UXAnalyticsDashboard from "./UXAnalyticsDashboard";
import { initUXTracker, trackPageView } from "./uxTracker";
import TradingExampleWidget from "./TradingExampleModal";
import UnsubscribePage from "./UnsubscribePage";
import BottomNavBar from "./BottomNavBar";

/* ─── History API helpers ─────────────────────────────────────────────────── */
const STANDALONE_VIEWS = ["admin", "employee"]; // layouts sans Navbar/Footer

function encodeState({ view, selectedId = null, selectedMarket = null, legalPage = null, companyPage = null, resourcePage = null, authMode = null }) {
  return { view, selectedId, selectedMarket, legalPage, companyPage, resourcePage, authMode };
}

// ── Construction du chemin réel à partir de l'état de navigation ──────────
// /                     → markets (accueil)
// /markets/:id          → détail d'un marché
// /howto                → comment ça marche
// /support /wallet /positions /settings
// /cgu /privacy /legal /cookies /rewards         (ex-/legal/:page)
// /about /careers /press /partners /blog         (ex-/company/:page)
// /faq /glossary /api /status /contact           (ex-/resources/:page)
// Slugs à la racine plutôt que sous /legal, /company, /resources — un
// chemin plus court se référence généralement mieux (moins de segments
// entre la racine et le contenu).
// ?auth=login|signup    → reste en query string : c'est une modale superposée
//                          à la page courante, pas une page à part entière.
function buildPath(state) {
  let path = "/";
  switch (state.view) {
    case "markets":
      path = state.selectedId ? `/markets/${state.selectedId}` : "/";
      break;
    case "howto":     path = "/howto";     break;
    case "support":   path = "/support";   break;
    case "settings":  path = "/settings";  break;
    case "positions": path = "/positions"; break;
    case "wallet":    path = "/wallet";    break;
    case "legal":     path = state.legalPage    ? `/${state.legalPage}`    : "/legal";     break;
    case "company":   path = state.companyPage  ? `/${state.companyPage}`  : "/company";   break;
    case "resources": path = state.resourcePage ? `/${state.resourcePage}` : "/resources"; break;
    default:          path = "/";
  }
  const params = new URLSearchParams();
  if (state.authMode) params.set("auth", state.authMode);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

function pushNav(state) {
  if (STANDALONE_VIEWS.includes(state.view)) return; // pas d'URL pour admin/employee
  window.history.pushState(state, "", buildPath(state));
}

// Slugs pris en charge par chaque catégorie — utilisés par parsePath() pour
// savoir à quelle catégorie rattacher un chemin plat comme /careers.
// Aucun chevauchement entre catégories (vérifié) ; le slug "legal" existe
// à la fois comme catégorie ET comme sous-page (mentions légales) — pas
// de conflit puisque /legal sans second segment retombe sur la catégorie.
const LEGAL_SLUGS    = ["cgu", "privacy", "legal", "cookies", "rewards"];
const COMPANY_SLUGS  = ["about", "careers", "press", "partners", "blog"];
const RESOURCE_SLUGS = ["faq", "glossary", "api", "status", "contact"];

// ── Lecture de l'état de navigation à partir du chemin réel ───────────────
function parsePath() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const authMode = new URLSearchParams(window.location.search).get("auth") || null;
  const [first, second] = parts;

  const base = { selectedId: null, legalPage: null, companyPage: null, resourcePage: null, authMode };

  switch (first) {
    case undefined: return { view: "markets", ...base };
    case "markets": return { view: "markets", ...base, selectedId: second ? parseInt(second, 10) : null };
    case "howto":     return { view: "howto",     ...base };
    case "support":   return { view: "support",   ...base };
    case "settings":  return { view: "settings",  ...base };
    case "positions": return { view: "positions", ...base };
    case "wallet":    return { view: "wallet",    ...base };
    // Repli si un lien vers la catégorie sans sous-page traîne quelque part
    case "company":   return { view: "company",   ...base };
    case "resources": return { view: "resources", ...base };
    default:
      if (LEGAL_SLUGS.includes(first))    return { view: "legal",     ...base, legalPage:    first };
      if (COMPANY_SLUGS.includes(first))  return { view: "company",   ...base, companyPage:  first };
      if (RESOURCE_SLUGS.includes(first)) return { view: "resources", ...base, resourcePage: first };
      return { view: "markets", ...base }; // chemin inconnu → accueil
  }
}

/* ─── App ─────────────────────────────────────────────────────────────────── */
function AppInner() {
  const init = parsePath();

  const [view,          setView]          = useState(init.view);
  const [selectedId,    setSelectedId]    = useState(init.selectedId);
  const [selectedMarket,setSelectedMarket]= useState(null);
  const [autoTradeIntent,setAutoTradeIntent]= useState(null); // {side,outcomeId} — vente directe depuis une position
  const [tradeMarket,   setTradeMarket]   = useState(null);
  const [authMode,      setAuthMode]      = useState(init.authMode);
  const [showContact,   setShowContact]   = useState(false);
  const [user,          setUser]          = useState(null);
  const [legalPage,     setLegalPage]     = useState(init.legalPage);
  const [companyPage,   setCompanyPage]   = useState(init.companyPage);
  const [resourcePage,  setResourcePage]  = useState(init.resourcePage);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { lang, setLang } = useI18n();

  // BottomNavBar/tracking attendent "markets" | "positions" | "wallet" | "support"
  // — `view` est déjà exactement ça, plus besoin de re-parser l'URL.
  const currentRoute = view;
  // ── Appliquer la langue du profil ────────────────────────────────────────
  const applyUserLanguage = useCallback(async () => {
    try {
      const r = await getProfile();
      if (r.data?.language) setLang(r.data.language);
    } catch {}
  }, [setLang]);

useEffect(() => { initUXTracker(); }, []);
useEffect(() => { trackPageView(currentRoute); }, [currentRoute]);


  // ── Restaurer session au chargement ──────────────────────────────────────
  useEffect(() => {
    const token         = localStorage.getItem("access_token");
    const username      = localStorage.getItem("username");
    const isAdmin       = localStorage.getItem("is_admin") === "true";
    const isStaff       = localStorage.getItem("is_staff") === "true";
    const role          = localStorage.getItem("role");
    const refresh_token = localStorage.getItem("refresh_token");

    if (token && username) {
      setUser({ username, isAdmin, isStaff, role, refresh_token });
      applyUserLanguage();
    }

    // Initialiser l'entrée history pour la page courante
    const curState = encodeState({ view: init.view, selectedId: init.selectedId, legalPage: init.legalPage, companyPage: init.companyPage, resourcePage: init.resourcePage, authMode: init.authMode });
    window.history.replaceState(curState, "", window.location.href);
  }, []);

  // ── Écouter le bouton ← → du navigateur ─────────────────────────────────
  useEffect(() => {
    const onPop = (e) => {
      const s = e.state;
      if (!s) {
        setView("markets"); setSelectedId(null); setSelectedMarket(null);
        setLegalPage(null); setCompanyPage(null); setResourcePage(null);
        setAuthMode(null);
        window.scrollTo({ top: 0, behavior: "instant" });
        return;
      }
      setView(s.view           || "markets");
      setSelectedId(s.selectedId     || null);
      setSelectedMarket(s.selectedMarket || null);
      setLegalPage(s.legalPage     || null);
      setCompanyPage(s.companyPage   || null);
      setResourcePage(s.resourcePage  || null);
      setAuthMode(s.authMode       || null);
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  if (ref) {
    // Stocker pour utilisation à l'inscription (valable 30 jours)
    const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
    localStorage.setItem("evenmt_referral_code", ref.toUpperCase());
    localStorage.setItem("evenmt_referral_expiry", String(expiry));

    // Optionnel : nettoyer l'URL pour pas que le user voie ?ref= partout
    // window.history.replaceState({}, "", window.location.pathname);
  }
}, []);

useEffect(() => {
  // Capter ?ref=XXX depuis l'URL et le persister 30 jours en localStorage
  const params = new URLSearchParams(window.location.search);
  const ref    = params.get("ref");
  if (ref) {
    const trimmed = ref.trim().toUpperCase();
    if (/^[A-Z0-9]{4,12}$/.test(trimmed)) {
      localStorage.setItem("referral_code", trimmed);
      localStorage.setItem("referral_code_at", String(Date.now()));
    }
    // Nettoyer l'URL (sans recharger)
    params.delete("ref");
    const cleanSearch = params.toString();
    const newUrl = window.location.pathname + (cleanSearch ? "?" + cleanSearch : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }
 
  // Purger le code de parrainage s'il a plus de 30 jours
  const ts = parseInt(localStorage.getItem("referral_code_at") || "0", 10);
  if (ts && Date.now() - ts > 30 * 24 * 3600 * 1000) {
    localStorage.removeItem("referral_code");
    localStorage.removeItem("referral_code_at");
  }
}, []);

  // ── Fonction navigate centrale ────────────────────────────────────────────
  const navigate = useCallback((v, opts = {}) => {
    const {
      selectedId    = null,
      selectedMarket= null,
      legalPage     = null,
      companyPage   = null,
      resourcePage  = null,
    } = opts;

    setView(v);
    setSelectedId(selectedId);
    setSelectedMarket(selectedMarket);
    setLegalPage(legalPage);
    setCompanyPage(companyPage);
    setResourcePage(resourcePage);

    pushNav(encodeState({ view: v, selectedId, selectedMarket, legalPage, companyPage, resourcePage }));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  // Ouvre/ferme la modale ET met à jour l'URL (?auth=login / ?auth=signup),
  // pour avoir un lien partageable/indexable plutôt qu'un simple state local.
  const openAuth = useCallback((mode) => {
    setAuthMode(mode);
    pushNav(encodeState({ view, selectedId, selectedMarket, legalPage, companyPage, resourcePage, authMode: mode }));
  }, [view, selectedId, selectedMarket, legalPage, companyPage, resourcePage]);

  const closeAuth = useCallback(() => {
    setAuthMode(null);
    pushNav(encodeState({ view, selectedId, selectedMarket, legalPage, companyPage, resourcePage, authMode: null }));
  }, [view, selectedId, selectedMarket, legalPage, companyPage, resourcePage]);

  // ── SEO : titre + meta description dynamiques selon la vue affichée ───────
  const seoMeta = getSeoMeta({ view, legalPage, companyPage, resourcePage, authMode, selectedMarket, lang });

  const handleAuth = async (userData) => {
    setUser(userData);
    localStorage.setItem("username", userData.username);
    localStorage.setItem("is_admin", userData.isAdmin ? "true" : "false");
    applyUserLanguage();
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.clear();
    navigate("markets");
  };

  // Dans App.jsx, au tout début du render
if (window.location.pathname === "/unsubscribe") {
  return (
        <>
        <Helmet><title>Dés inscription — EVPTrading</title><meta name="description" content="Gérez vos préférences d'e-mail EVPTrading." /></Helmet>
        <GlobalStyle />
  <UnsubscribePage />
        </>
);
}

  // ── Vues standalone (pas de Navbar/Footer/History) ───────────────────────
  if (view === "employee") {
    return (
      <>
        <Helmet><title>Espace employé —EVPTrading</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <GlobalStyle />
        <EmployeeDashboard
          user={user}
          onLogout={handleLogout}
          onHome={() => navigate("markets")}
        />
      </>
    );
  }

  if (view === "admin") {
    return (
      <>
        <Helmet><title>Administration —EVPTrading</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <GlobalStyle />
        <ToastProvider>
          <AdminDashboard
            user={user}
            onLogout={handleLogout}
            onBack={() => navigate("markets")}
          />
        </ToastProvider>
        <UXAnalyticsDashboard />
      </>
    );
  }

  // ── Vue principale ────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{seoMeta.title}</title>
        <meta name="description" content={seoMeta.description} />
      </Helmet>
      <GlobalStyle />
      <ToastProvider>
        <Navbar
          view={view}
          setView={v => navigate(v)}
          navigate={navigate}
          setDrawerOpen={setDrawerOpen}
          drawerOpen={drawerOpen}
          user={user}
          onLoginClick={(mode) => openAuth(mode)}
          onLogout={handleLogout}
        />
        {view === "markets" && !selectedId && (
            <TradingExampleWidget
              user={user}
              onLoginClick={() => openAuth("login")}
              onSignupClick={() => openAuth("signup")}
              onNavigateToMarkets={() => navigate("markets")}
            />
          )}

          <BottomNavBar
                activeRoute={currentRoute}      // "markets" | "positions" | "wallet" | "support"
                user={user}
                lang={lang}
                onNavigate={(route) => navigate(route)}
                onOpenMenu={() => setDrawerOpen(true)}
              />

        {view === "markets" && !selectedId && (
          <MarketList
          user={user}
          onNavigate={(view) => setView(view)} 
            onSelectMarket={(m) => navigate("markets", { selectedId: m.id, selectedMarket: m })}
          onLoginClick={(mode) => openAuth(mode)}
            onTradeMarket={m => {
              if (!user) { openAuth("login"); return; }
              setTradeMarket(m);
            }}
          />
        )}
        
        {view === "markets" && selectedId && (
          <MarketDetail
            marketId={selectedId}
            initialMarket={selectedMarket}
            user={user}
            onBack={() => window.history.back()}
            onLoginClick={() => openAuth("login")}
            autoTrade={autoTradeIntent}
            onAutoTradeConsumed={() => setAutoTradeIntent(null)}
          />
        )}

        {view === "support" && (
          <MyTickets user={user} onLoginClick={() => openAuth("login")} />
        )}

        {view === "settings" && (
          <AccountSettings user={user} onLogout={handleLogout} />
        )}

        {view === "positions" && (
          <PositionList
            user={user}
            onGoToMarket={id => navigate("markets", { selectedId: id })}
            onSellPosition={pos => {
              setAutoTradeIntent({ side: "SELL", outcomeId: pos.outcome_id });
              navigate("markets", { selectedId: pos.market_id, selectedMarket: null });
            }}
            onLoginClick={() => openAuth("login")}
          />
        )}

        {view === "wallet"    && <WalletPage user={user} onLoginClick={() => openAuth("login")} />}

        {view === "legal"     && <LegalPages initialPage={legalPage} />}

        {view === "company"   && <CompanyPages initialPage={companyPage} />}

        {view === "resources" && (
          <ResourcePages
            initialPage={resourcePage}
            onNavigate={(p) => {
              if (["support","company","howto"].includes(p)) navigate(p);
              else navigate("resources", { resourcePage: p });
            }}
          />
        )}

        {view === "howto" && (
          <HowItWorks
            onSignup={() => openAuth("signup")}
            onLogin={() => openAuth("login")}
            onExplore={() => navigate("markets")}
          />
        )}

        {tradeMarket && (
          <TradeModal
            market={tradeMarket}
            user={user}
            onClose={() => setTradeMarket(null)}
          />
        )}

        {showContact && (
          <ContactModal
            user={user}
            onClose={() => setShowContact(false)}
            onSuccess={() => setShowContact(false)}
          />
        )}

        {authMode && (
          <AuthModal
            initialMode={authMode}
            onClose={closeAuth}
            onAuth={handleAuth}
          />
        )}

        <Footer
          onNavigate={navigate}
          onLegal={(page) => navigate("legal", { legalPage: page })}
          onCompany={(page) => navigate("company", { companyPage: page })}
          onResource={(page) => navigate("resources", { resourcePage: page })}
        />
      </ToastProvider>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AppInner />
    </HelmetProvider>
  );
}
